import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Coins, Loader2, Send } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { num, parseNum, rp } from "../../lib/format";
import { enablePush, pushStatus } from "../../lib/loyalty";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

const KEYS = ["loyalty_enabled", "points_per_amount", "point_value", "notify_large_sale", "notify_shift_close", "notify_email"];

export const LoyaltyNotifTab = () => {
  const { store, setStore, can } = useAuth();
  const [f, setF] = useState(null);
  const [push, setPush] = useState("off");
  const [busy, setBusy] = useState("");
  useEffect(() => { if (store) setF(Object.fromEntries(KEYS.map((k) => [k, store[k]]))); }, [store]);
  useEffect(() => { pushStatus().then(setPush); }, []);
  if (!f) return null;

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/settings", { ...f, points_per_amount: Number(f.points_per_amount) || 10000, point_value: Number(f.point_value) || 0, notify_large_sale: Number(f.notify_large_sale) || 0 });
      setStore(data);
      toast.success("Pengaturan disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const turnOnPush = async () => {
    setBusy("push");
    try {
      await enablePush(api);
      setPush("on");
      toast.success("Notifikasi HP aktif di perangkat ini");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const test = async () => {
    try { await api.post("/push/test"); toast.success("Notifikasi tes dikirim"); } catch (e) { toast.error(errMsg(e)); }
  };
  const numField = (k, label, hint, testid) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input inputMode="numeric" className="font-mono" value={num(f[k])} onChange={(e) => setF({ ...f, [k]: parseNum(e.target.value) })} data-testid={testid} />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );

  return (
    <div className="grid xl:grid-cols-2 gap-6 items-start max-w-5xl">
      <div className="rounded-2xl border bg-card p-5 space-y-4">
        <div className="flex gap-3"><div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center"><Coins className="h-5 w-5" /></div>
          <div><h2 className="font-display font-bold">Program Member & Poin</h2><p className="text-sm text-muted-foreground">Pelanggan mengumpulkan poin dari belanja dan menukarnya sebagai potongan harga.</p></div></div>
        <label className="flex items-center justify-between text-sm"><span>Aktifkan program poin</span><Switch checked={!!f.loyalty_enabled} onCheckedChange={(v) => setF({ ...f, loyalty_enabled: v })} data-testid="loyalty-enabled-switch" /></label>
        <div className="grid sm:grid-cols-2 gap-4">
          {numField("points_per_amount", "Belanja per 1 poin (Rp)", `Belanja ${rp(f.points_per_amount)} = 1 poin`, "points-per-amount-input")}
          {numField("point_value", "Nilai tukar 1 poin (Rp)", `1 poin = potongan ${rp(f.point_value)}`, "point-value-input")}
        </div>
        <p className="text-xs text-muted-foreground">Level otomatis: Bronze, Silver (belanja ≥ Rp 1 jt), Gold (≥ Rp 5 jt).</p>
      </div>
      {can("reports") && (
        <div className="rounded-2xl border bg-card p-5 space-y-4">
          <div className="flex gap-3"><div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center"><BellRing className="h-5 w-5" /></div>
            <div><h2 className="font-display font-bold">Notifikasi Owner</h2><p className="text-sm text-muted-foreground">Kirim notifikasi ke HP owner untuk penjualan besar & tutup shift.</p></div></div>
          {numField("notify_large_sale", "Notifikasi jika penjualan ≥ (Rp)", f.notify_large_sale ? `Aktif untuk transaksi ≥ ${rp(f.notify_large_sale)}` : "0 = nonaktif", "notify-large-sale-input")}
          <label className="flex items-center justify-between text-sm"><span>Notifikasi saat kasir tutup shift</span><Switch checked={!!f.notify_shift_close} onCheckedChange={(v) => setF({ ...f, notify_shift_close: v })} data-testid="notify-shift-close-switch" /></label>
          <label className="flex items-center justify-between text-sm"><span>Kirim juga ke email owner</span><Switch checked={f.notify_email !== false} onCheckedChange={(v) => setF({ ...f, notify_email: v })} data-testid="notify-email-switch" /></label>
          <div className="rounded-xl bg-muted/60 p-3 space-y-2">
            <div className="text-sm">Status di perangkat ini: <b data-testid="push-status">{push === "on" ? "Aktif" : push === "unsupported" ? "Tidak didukung" : "Belum aktif"}</b></div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={turnOnPush} disabled={busy === "push" || push === "unsupported"} data-testid="enable-push-btn">{busy === "push" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}<BellRing className="h-4 w-4 mr-1" />Aktifkan di HP ini</Button>
              <Button size="sm" variant="outline" onClick={test} data-testid="test-push-btn"><Send className="h-4 w-4 mr-1" />Kirim Tes</Button>
            </div>
            <p className="text-xs text-muted-foreground">Buka MUFA POS di HP owner lalu tekan "Aktifkan di HP ini". Android: Chrome. iPhone: instal ke Layar Utama dulu (iOS 16.4+).</p>
          </div>
        </div>
      )}
      <Button className="w-fit" onClick={save} disabled={busy === "save"} data-testid="loyalty-save-btn">{busy === "save" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Simpan Pengaturan</Button>
    </div>
  );
};
